<script setup lang="ts">
import {
	inject,
	ref,
	onUnmounted,
	onMounted,
	computed,
	watch,
	nextTick,
} from 'vue'
import type { Ref } from 'vue'
import { reactive } from 'vue'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetFooter,
	SheetTitle,
	SheetTrigger,
} from '@/components/ui/sheet'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { useRouter, useRoute } from 'vue-router'
import MediaMetaData from '@/components/MediaMetaData.vue'
import MediaThumb from '@/components/MediaThumb.vue'
import TagDisplay from '@/components/TagDisplay.vue'
import DeepSafeScan from '@/components/DeepSafeScan.vue'
import {
	ArrowUpTrayIcon,
	ChevronDownIcon,
	PencilSquareIcon,
	PhotoIcon,
	MapPinIcon,
	ShieldCheckIcon,
	CloudArrowDownIcon,
	ExclamationTriangleIcon,
	QuestionMarkCircleIcon,
	EyeSlashIcon,
	DocumentDuplicateIcon,
	ArrowPathIcon,
	LinkIcon,
} from '@heroicons/vue/24/solid'
import type { Member } from '@/types/Member'
import axios from 'axios'
import type { FileMeta, MetaGroup } from '@/types/Media'
import { defaultMedia } from '@/types/Media'
import type { Media } from '@/types/Media'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
	Check, X, Trash2, RefreshCw, CloudUpload, Eye, EyeOff,
	Copy, Upload, Replace, GitBranch, Image, Scissors, Clock,
	ChevronDown, Info, Save, Loader2, FileText, Languages,
} from 'lucide-vue-next'
import { toast } from 'vue-sonner'
import 'video.js/dist/video-js.css'
import videojs from 'video.js'
import { useTags } from '@/services/Tags'
import { useMedia } from '@/services/Media'
import useClientTransfer from '@/services/clientTransfer'
import { sendGraphQLQuery } from '@/services/gql-util'
import vhostService from '@/services/Vhost'
import useAITranslate from '@/services/aiTranslate'
import type { AITranslateField, AITranslateAvailableSource } from '@/services/aiTranslate'

const route = useRoute()
const router = useRouter()
const vhostId = inject<Ref<string>>('vhostId', ref('0'))
const {
	getCustomMeta: gqlGetCustomMeta,
	getCustomMetaProviders: gqlGetCustomMetaProviders,
} = vhostService()
const { getMediaDetail, fetchMedia, setModerationStatus, trashFile, queueReconversion } = useMedia()
const { transferList, getClientTransferList } = useClientTransfer()

const selectedTab = ref(0)
const loaded = ref(false)
const vhostMismatch = ref(false)
const selectedVersion = ref('')
const metaProviders = ref<{ provider: string; vhost: number; id: number }[]>([])
const metaDialogOpen = ref(false)
const mediaInfo = reactive<Media>(defaultMedia)
const bulkMetaData = reactive<Record<string, FileMeta>>({})
const defaultData = ref<MetaGroup[]>([])
const metaProviderTab = ref(0)
const checkBoxGroups = ref<Record<string, unknown>>({})

// ─── Inline editing state ───
const editingTitle = ref(false)
const editingDescription = ref(false)
const editTitleValue = ref('')
const editDescriptionValue = ref('')
const savingField = ref(false)

// ─── Thumbnail chooser ───
const showThumbDialog = ref(false)
const thumbTimecode = ref('')
const settingThumb = ref(false)
const thumbVideoEl = ref<HTMLVideoElement | null>(null)
const thumbPlayer = ref<any>(null)
const thumbCurrentTime = ref('00:00:00')

// ─── Version state ───
const versions = ref<{ version: string; label: string; created?: string }[]>([])
const showVersions = ref(false)

// ─── Additional info toggle ───
const showAdditionalInfo = ref(false)

// ─── Upload child / replace dialog ───
const showUploadChild = ref(false)
const showReplaceAsset = ref(false)
const showAssignParent = ref(false)
const assignParentId = ref('')

const mediaId = computed(() => {
	const id = route.params.id
	return Array.isArray(id) ? id[0] : id ?? ''
})

const userInfo = inject<Ref<Member> | null>('userInfo', null)
const breadcrumb = inject<Ref<string>>('breadcrumb', ref('No Title'))
const mmMode = inject<Ref<boolean>>('mmMode', ref(false))
const { tagList, updateTags } = useTags({
	initialTags: [],
	mediaItem: { id: mediaId.value },
})

// ─── User meta prefs ───
const hiddenMetaFields = computed<string[]>(() => {
	const meta = userInfo?.value?.meta
	if (!meta?.hiddenMetaFields) return []
	return Array.isArray(meta.hiddenMetaFields) ? meta.hiddenMetaFields : []
})

// ─── Related tab state ───
const relatedSubTab = ref<'children' | 'subtitles'>('children')
const childMedia = ref<Media[]>([])
const captionMedia = ref<Media[]>([])
const childrenLoaded = ref(false)

// ─── Location tab state ───
const mapContainer = ref<HTMLElement | null>(null)
const showMap = ref(false)
const mapInstance = ref<unknown>(null)
const mapMarker = ref<unknown>(null)

// mmMode shows fewer tabs — no Location, no Verify
const mediaMenuOptions = computed(() => {
	if (mmMode.value) {
		return [
			{ label: 'Details', value: 0 },
			{ label: 'Related', value: 1 },
			{ label: 'Metadata', value: 3 },
		]
	}
	return [
		{ label: 'Details', value: 0 },
		{ label: 'Related', value: 1 },
		{ label: 'Location', value: 2 },
		{ label: 'Metadata', value: 3 },
		{ label: 'Verify', value: 4 },
	]
})

// ─── Computed helpers ───
const moderationLabel = computed(() => {
	const s = mediaInfo.moderationStatus
	if (s === 1) return 'Approved'
	if (s === 2) return 'Unmoderated'
	if (s === 3) return 'Denied'
	return 'Unknown'
})

const moderationBadgeClass = computed(() => {
	const s = mediaInfo.moderationStatus
	if (s === 1) return 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30'
	if (s === 3) return 'bg-red-500/15 text-red-700 border-red-500/30'
	return 'bg-amber-500/15 text-amber-700 border-amber-500/30'
})

const statusLabel = computed(() => {
	const s = mediaInfo.status
	if (s === 1) return 'Processing'
	if (s === 2) return 'Converting'
	if (s === 3) return 'Active'
	if (s === 4) return 'Active'
	if (s === 5) return 'Trashed'
	if (s === 6) return 'Hidden'
	return 'Unknown'
})

const filetypeLabel = computed(() => {
	const ft = mediaInfo.filetype
	if (ft === 1) return 'Image'
	if (ft === 2) return 'Video'
	if (ft === 3) return 'Audio'
	if (ft === 4) return 'Document'
	return `Type ${ft}`
})

const isVideo = computed(() => mediaInfo.filetype === 2)
const isImage = computed(() => mediaInfo.filetype === 1)
const isTrashed = computed(() => mediaInfo.status === 5)
const isHidden = computed(() => mediaInfo.status === 6)

// Authenticity verdict computed from system metadata
const authenticityVerdict = computed(() => {
	const sys = mediaInfo.metadata?.system
	if (!sys) return null
	const fd = sys['fake_detection']
	if (!fd) return null
	const detection = typeof fd === 'string'
		? (() => { try { return JSON.parse(fd) } catch { return null } })()
		: fd
	if (!detection?.editor_tag) return null
	const tag = detection.editor_tag
	if (tag === 'likely_real') return { label: 'Authentic', classes: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30', icon: ShieldCheckIcon }
	if (tag === 'likely_fake' || tag === 'fake') return { label: 'Manipulated', classes: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30', icon: ExclamationTriangleIcon }
	if (tag === 'synthetic_likely' || tag === 'synthetic') return { label: 'Synthetic Likely', classes: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30', icon: ExclamationTriangleIcon }
	if (tag === 'uncertain' || tag === 'inconclusive') return { label: 'Inconclusive', classes: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30', icon: QuestionMarkCircleIcon }
	const prettyLabel = tag.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
	return { label: prettyLabel, classes: 'bg-muted text-muted-foreground border-muted', icon: QuestionMarkCircleIcon }
})

const mediaInfoPolling = ref<number | null>(null)

const stripHtml = (input: string): string => {
	const parser = new DOMParser()
	const doc = parser.parseFromString(input, 'text/html')
	return doc.body.textContent || ''
}

const formatDate = (dateStr: string): string => {
	if (!dateStr) return '—'
	try {
		const d = new Date(dateStr)
		return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
	} catch { return dateStr }
}

// ─── Metadata functions ───
const getMetaProvider = async () => {
	metaProviders.value = await gqlGetCustomMetaProviders(Number(mediaInfo.vhost))
	await loadCustomMeta(0)
}

const loadCustomMeta = async (newTab = 0) => {
	try {
		const items: FileMeta[] = await gqlGetCustomMeta(Number(mediaInfo.vhost), Number(mediaInfo.id))
		const tmp: Record<string, Record<string, FileMeta[]>> = {}
		items.forEach((item) => {
			const provider = item.provider || 'none'
			const group = item.grouping || 'default'
			if (!tmp[provider]) tmp[provider] = {}
			if (!tmp[provider][group]) tmp[provider][group] = []
			tmp[provider][group].push(item)
		})
		const groups: MetaGroup[] = []
		Object.keys(tmp).forEach((provider) => {
			Object.keys(tmp[provider]).forEach((groupName) => {
				groups.push({
					provider,
					groupName: groupName === 'default' ? undefined : groupName,
					items: tmp[provider][groupName],
				})
			})
		})
		defaultData.value = groups
		metaProviderTab.value = newTab
	} catch (error) {
		console.error('Error fetching custom meta:', error)
	}
}

const saveMetadata = async () => {
	try {
		const metaToSave: Record<string, unknown> = {}
		for (const [key, val] of Object.entries(mediaInfo.metadata)) {
			if (key !== 'exif' && key !== 'system' && key !== 'request' && key !== 'xmp' && key !== 'iptc') {
				metaToSave[key] = val
			}
		}
		await axios.post('/services/json', {
			method: 'media.updateMediaData',
			id: mediaInfo.id,
			metadata: JSON.stringify(metaToSave),
		})
		toast.success('Metadata saved')
	} catch (error) {
		toast.error('Failed to save metadata')
		console.error('Error saving metadata:', error)
	}
}

// ─── Media detail fetch ───
const getMediaInfo = async () => {
	try {
		const result = await getMediaDetail(Number(mediaId.value), selectedVersion.value)
		if (!result) {
			console.warn('getMediaInfo: no result for media', mediaId.value)
			return
		}
		Object.assign(mediaInfo, { ...result })
		loaded.value = true
		if (vhostId.value && mediaInfo.vhost && String(mediaInfo.vhost) !== String(vhostId.value)) {
			vhostMismatch.value = true
			stopMediaInfoPolling()
			return
		}
		breadcrumb.value = mediaInfo?.title ?? 'No Title'
		if (mediaInfo.tags) {
			tagList.value = mediaInfo.tags
				? mediaInfo.tags.split(' ').filter((tag) => tag.trim() !== '')
				: []
		}
		if (mediaInfo.status === 1 || mediaInfo.status === 2) {
			if (mediaInfoPolling.value !== null) clearInterval(mediaInfoPolling.value)
			mediaInfoPolling.value = window.setInterval(getMediaInfo, 2500)
		} else {
			if (typeof mediaInfo.statusInfo !== 'undefined') {
				mediaInfo.statusInfo.conversion_percentage = 100
			}
			stopMediaInfoPolling()
		}
	} catch (error) {
		console.error('Error fetching media info:', error)
		if (typeof mediaInfo.statusInfo !== 'undefined') {
			mediaInfo.statusInfo.conversion_percentage = 100
		}
		stopMediaInfoPolling()
	}
}

const handleTagAdded = () => { updateTags() }
const handleTagRemoved = () => { updateTags() }

const stopMediaInfoPolling = () => {
	if (mediaInfoPolling.value !== null) {
		clearInterval(mediaInfoPolling.value)
		mediaInfoPolling.value = null
	}
}

// ─── Related tab: fetch children ───
const fetchChildren = async () => {
	if (childrenLoaded.value || !mediaInfo.id || !mediaInfo.vhost) return
	try {
		const result = await fetchMedia(
			Number(mediaInfo.vhost),
			{ parentid: Number(mediaInfo.id), moderationStatus: 'all', context: 'all', status: [3, 4], filetype: [1, 2, 3, 4] },
			'UPLOAD DESC', 0, 100,
			['id', 'title', 'message', 'extension', 'contenttype', 'filetype', 'thumbUrl', 'status', 'user_firstname', 'user_lastname', 'upload'],
		)
		const allChildren = result?.mediaData || []
		const captionExts = ['srt', 'vtt', 'sbv', 'sub', 'ass']
		childMedia.value = allChildren.filter((m: Media) => !captionExts.includes(m.extension?.toLowerCase() || ''))
		captionMedia.value = allChildren.filter((m: Media) => captionExts.includes(m.extension?.toLowerCase() || ''))
		childrenLoaded.value = true
	} catch (error) {
		console.error('Error fetching children:', error)
	}
}

// ─── Location tab: init Leaflet map ───
const initMap = async () => {
	if (mapInstance.value) return
	const L = await import('leaflet')
	await nextTick()
	if (!mapContainer.value) return
	const lat = Number(mediaInfo.geo_latitude) || 43.6532
	const lng = Number(mediaInfo.geo_longitude) || -79.3832
	const hasGeo = Boolean(mediaInfo.geo_latitude && mediaInfo.geo_longitude)
	const map = L.map(mapContainer.value).setView([lat, lng], hasGeo ? 14 : 3)
	L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
		attribution: '&copy; OpenStreetMap contributors',
		maxZoom: 19,
	}).addTo(map)
	if (hasGeo) mapMarker.value = L.marker([lat, lng]).addTo(map)
	map.on('click', (e: { latlng: { lat: number; lng: number } }) => {
		const { lat: clickLat, lng: clickLng } = e.latlng
		if (mapMarker.value) {
			(mapMarker.value as { setLatLng: (ll: [number, number]) => void }).setLatLng([clickLat, clickLng])
		} else {
			mapMarker.value = L.marker([clickLat, clickLng]).addTo(map)
		}
		mediaInfo.geo_latitude = String(clickLat)
		mediaInfo.geo_longitude = String(clickLng)
	})
	mapInstance.value = map
}

// ─── Child type helpers ───
const getChildTypeLabel = (child: Media): string => {
	const ext = (child.extension || '').toLowerCase()
	if (ext === 'srt' || ext === 'vtt' || ext === 'sbv') return 'Caption'
	if (child.filetype === 1) return 'Image'
	if (child.filetype === 2) return 'Video'
	if (child.filetype === 3) return 'Audio'
	return 'File'
}

const getChildThumbSrc = (child: Media): string => {
	if (child.filetype === 4) return 'https://assets.newspark.ca/2/text-thumb.png'
	if (child.filetype === 3) return 'https://assets.newspark.ca/2/v5/gfx/audio.png'
	if (child.thumbUrl) return `${child.thumbUrl}/12`
	return 'https://assets.newspark.ca/2/v5/gfx/failed.png'
}

// Watch for tab changes to lazy-load data
watch(selectedTab, (tab) => {
	if (tab === 1 && !childrenLoaded.value && mediaInfo.id) fetchChildren()
	if (tab === 2) {
		showMap.value = true
		nextTick(() => { initMap() })
	}
})

onUnmounted(() => {
	stopMediaInfoPolling()
	disposeThumbPlayer()
	if (mapInstance.value) {
		(mapInstance.value as { remove: () => void }).remove()
		mapInstance.value = null
	}
})

// ─── Single-item actions ───
const approveMedia = async () => {
	try { await setModerationStatus(Number(mediaId.value), 1); toast.success('Media approved'); await getMediaInfo() }
	catch { toast.error('Failed to approve') }
}
const denyMedia = async () => {
	try { await setModerationStatus(Number(mediaId.value), 3); toast.success('Media denied'); await getMediaInfo() }
	catch { toast.error('Failed to deny') }
}
const unmoderateMedia = async () => {
	try { await setModerationStatus(Number(mediaId.value), 2); toast.success('Media unmoderated'); await getMediaInfo() }
	catch { toast.error('Failed to unmoderate') }
}
const trashMedia = async () => {
	try { await trashFile(Number(mediaId.value)); toast.success('Media trashed'); router.push('/media') }
	catch { toast.error('Failed to trash') }
}
const untrashMedia = async () => {
	try {
		await axios.post('/services/json', { method: 'media.untrashFile', id: mediaInfo.id })
		toast.success('Media restored')
		await getMediaInfo()
	} catch { toast.error('Failed to restore') }
}
const hideMedia = async () => {
	try {
		await axios.post('/services/json', { method: 'media.hideFile', id: mediaInfo.id })
		toast.success('Media hidden')
		await getMediaInfo()
	} catch { toast.error('Failed to hide') }
}
const cloneMediaData = async () => {
	try {
		await axios.post('/services/json', { method: 'media.cloneMediaData', id: mediaInfo.id })
		toast.success('Media data cloned')
	} catch { toast.error('Failed to clone') }
}
const permanentlyDelete = async () => {
	if (!confirm('Are you sure you want to PERMANENTLY delete this media? This cannot be undone.')) return
	try {
		await axios.post('/services/json', { method: 'media.deleteFile', id: mediaInfo.id })
		toast.success('Media permanently deleted')
		router.push('/media')
	} catch { toast.error('Failed to delete') }
}
const reconvertMedia = async () => {
	try { await queueReconversion(Number(mediaId.value)); toast.success('Queued for re-conversion') }
	catch { toast.error('Failed to queue re-conversion') }
}
const publishToTransfer = async (transferId: number, transferName: string) => {
	try {
		const mutation = `mutation ($mediaIds: [Int!]!, $transferId: Int!) { AssetMutations { publishToTransfer(mediaIds: $mediaIds, transferId: $transferId) } }`
		await sendGraphQLQuery(mutation, { mediaIds: [Number(mediaId.value)], transferId })
		toast.success(`Publishing to ${transferName}`)
	} catch { toast.error('Failed to publish') }
}
const isTransferSupported = (transfer: any) => {
	const ft = Number(mediaInfo.filetype ?? 0)
	const tft = Number(transfer.filetype ?? 0)
	return tft === 0 || tft === ft
}

// ─── Inline title/description editing ───
const startEditTitle = () => {
	editTitleValue.value = mediaInfo.title
	editingTitle.value = true
}
const saveTitle = async () => {
	savingField.value = true
	try {
		await axios.post('/services/json', { method: 'media.updateMediaData', id: mediaInfo.id, title: editTitleValue.value })
		mediaInfo.title = editTitleValue.value
		breadcrumb.value = editTitleValue.value
		editingTitle.value = false
		toast.success('Title updated')
	} catch { toast.error('Failed to update title') }
	finally { savingField.value = false }
}
const startEditDescription = () => {
	editDescriptionValue.value = stripHtml(mediaInfo.message)
	editingDescription.value = true
}
const saveDescription = async () => {
	savingField.value = true
	try {
		await axios.post('/services/json', { method: 'media.updateMediaData', id: mediaInfo.id, message: editDescriptionValue.value })
		mediaInfo.message = editDescriptionValue.value
		editingDescription.value = false
		toast.success('Description updated')
	} catch { toast.error('Failed to update description') }
	finally { savingField.value = false }
}

// ─── Set thumbnail from timecode ───
function formatTimecode(seconds: number): string {
	const h = Math.floor(seconds / 3600).toString().padStart(2, '0')
	const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0')
	const s = Math.floor(seconds % 60).toString().padStart(2, '0')
	return `${h}:${m}:${s}`
}

function getVideoSrc(): string {
	if (!mediaInfo.thumbUrl) return ''
	// thumbUrl is the base URL (no profile suffix), e.g. https://storage.newspark.ca/storage/38253094
	// Profile 22 is the streaming MP4
	return `${mediaInfo.thumbUrl}/22`
}

function initThumbPlayer() {
	// Dispose any previous player
	disposeThumbPlayer()
	nextTick(() => {
		const el = thumbVideoEl.value
		if (!el) return
		thumbPlayer.value = videojs(el, {
			controls: true,
			preload: 'auto',
			fluid: true,
			sources: [{ type: 'video/mp4', src: getVideoSrc() }],
		})
		thumbPlayer.value.on('timeupdate', () => {
			const t = thumbPlayer.value.currentTime() || 0
			thumbCurrentTime.value = formatTimecode(t)
			thumbTimecode.value = formatTimecode(t)
		})
	})
}

function disposeThumbPlayer() {
	if (thumbPlayer.value) {
		try { thumbPlayer.value.dispose() } catch { /* already disposed */ }
		thumbPlayer.value = null
	}
}

// Watch dialog open/close to init/dispose the player
watch(showThumbDialog, (open) => {
	if (open && isVideo.value) {
		initThumbPlayer()
	} else {
		disposeThumbPlayer()
	}
})

const setThumbnail = async () => {
	settingThumb.value = true
	try {
		await axios.post('/services/json', { method: 'media.setThumbnail', id: mediaInfo.id, timecode: thumbTimecode.value })
		toast.success('Thumbnail updated')
		showThumbDialog.value = false
		await getMediaInfo()
	} catch { toast.error('Failed to set thumbnail') }
	finally { settingThumb.value = false }
}

// ─── Assign parent ───
const doAssignParent = async () => {
	try {
		await axios.post('/services/json', { method: 'media.assignParent', id: mediaInfo.id, parentid: assignParentId.value })
		toast.success('Parent assigned')
		showAssignParent.value = false
		await getMediaInfo()
	} catch { toast.error('Failed to assign parent') }
}

// ─── AI Translate ───
const {
	getTranslatableFields,
	previewMediaFields,
	applyMediaFields,
	loading: translateLoading,
	error: translateError,
} = useAITranslate()

const showTranslateModal = ref(false)
const translateStep = ref<'fields' | 'preview' | 'done'>('fields')
const translateTargetLang = ref('')
const translateFields = ref<AITranslateField[]>([])
const translateAvailableSources = ref<AITranslateAvailableSource[]>([])
const translateSelectedSource = ref<string | null>(null)
const translateSelectedFields = ref<string[]>([])
const translatePreviews = ref<Record<string, string>>({})
const translateApplying = ref(false)

// Detect lang from metadata (widen convention: widen:Language or system.language)
const detectedLang = computed<string>(() => {
	const meta = mediaInfo.metadata
	if (!meta) return ''
	const widenLang = meta['widen:Language'] || meta['widen_language'] || meta['language']
	if (widenLang && typeof widenLang === 'string') {
		// Extract BCP47 code: "German, de_DE, DE" → "de"
		const parts = widenLang.split(',').map((s: string) => s.trim())
		for (const p of parts) {
			const m = p.match(/^([a-z]{2})[-_]/i)
			if (m) return m[1].toLowerCase()
			if (/^[a-z]{2}$/i.test(p)) return p.toLowerCase()
		}
	}
	const sysLang = meta?.system?.['language'] || meta?.system?.['detected_language']
	if (sysLang && typeof sysLang === 'string') return sysLang.split(/[-_]/)[0].toLowerCase()
	return ''
})

const translateButtonLabel = computed(() => {
	if (!detectedLang.value) return 'Translate'
	const names: Record<string, string> = {
		de: 'German', fr: 'French', es: 'Spanish', it: 'Italian', pt: 'Portuguese',
		nl: 'Dutch', pl: 'Polish', ru: 'Russian', ja: 'Japanese', zh: 'Chinese',
		ko: 'Korean', ar: 'Arabic', sv: 'Swedish', da: 'Danish', fi: 'Finnish',
		nb: 'Norwegian', cs: 'Czech', hu: 'Hungarian', ro: 'Romanian', tr: 'Turkish',
	}
	return `Translate to ${names[detectedLang.value] || detectedLang.value.toUpperCase()}`
})

const openTranslateModal = async () => {
	translateStep.value = 'fields'
	translatePreviews.value = {}
	translateSelectedFields.value = []
	translateAvailableSources.value = []
	translateSelectedSource.value = null
	if (detectedLang.value) translateTargetLang.value = detectedLang.value
	showTranslateModal.value = true
	await loadTranslatableFields()
}

const loadTranslatableFields = async () => {
	const result = await getTranslatableFields(
		vhostId.value,
		Number(mediaId.value),
		translateSelectedSource.value ?? undefined,
	)
	if (!result) return
	translateFields.value = result.fields || []
	translateAvailableSources.value = result.available_sources || []
	if (!translateSelectedSource.value && result.source?.kind) {
		translateSelectedSource.value = result.source.kind
	}
	if (!translateTargetLang.value && result.suggested_lang) {
		translateTargetLang.value = result.suggested_lang
	}
	// Auto-select title/message/tags
	translateSelectedFields.value = translateFields.value
		.filter((f) => ['title', 'message', 'tags'].includes(f.path))
		.map((f) => f.path)
}

const runTranslatePreview = async () => {
	if (!translateSelectedFields.value.length || !translateTargetLang.value) return
	const result = await previewMediaFields(
		vhostId.value,
		Number(mediaId.value),
		translateTargetLang.value,
		translateSelectedFields.value,
		translateSelectedSource.value ?? undefined,
	)
	if (!result) return
	translatePreviews.value = result.previews || {}
	translateStep.value = 'preview'
}

const applyTranslation = async () => {
	translateApplying.value = true
	try {
		const result = await applyMediaFields(
			vhostId.value,
			Number(mediaId.value),
			translateTargetLang.value,
			translateSelectedFields.value,
			translateSelectedSource.value ?? undefined,
		)
		if (result?.success) {
			toast.success('Translation applied')
			translateStep.value = 'done'
			await getMediaInfo()
		} else {
			toast.error('Translation failed')
		}
	} finally {
		translateApplying.value = false
	}
}

// ─── Version switching ───
const switchVersion = async (ver: string) => {
	selectedVersion.value = ver
	await getMediaInfo()
	toast.info(`Switched to version: ${ver || 'latest'}`)
}

// ─── Download ───
const downloadMedia = () => {
	if (mediaInfo.publicUrl) {
		window.open(mediaInfo.publicUrl, '_blank')
	} else {
		window.open(`/services/media/download/${mediaInfo.id}`, '_blank')
	}
}

onMounted(() => {
	getMediaInfo()
	getClientTransferList(vhostId.value)
})
</script>

<template>
	<!-- Vhost Mismatch -->
	<div v-if="vhostMismatch" class="flex items-center justify-center min-h-[60vh]">
		<div class="text-center space-y-4">
			<ShieldCheckIcon class="h-16 w-16 text-muted-foreground mx-auto opacity-40" />
			<h1 class="text-xl font-semibold text-foreground">Media Not Found</h1>
			<p class="text-muted-foreground text-sm max-w-md">
				This media item does not belong to the current project.
			</p>
			<Button variant="outline" @click="router.push('/media')">Back to Media</Button>
		</div>
	</div>

	<div v-else class="flex flex-col lg:flex-row border bg-card m-1 lg:m-3">
		<!-- Main Content Area — 70% on desktop -->
		<div v-if="loaded" class="w-full flex flex-col border-r lg:w-[70%] lg:min-w-0">
			<!-- Title Header -->
			<div class="p-2 lg:p-3 border-b flex flex-wrap justify-start items-center gap-2">
				<PhotoIcon class="h-5 w-5 lg:h-6 lg:w-6 shrink-0" />

				<!-- Inline title editing -->
				<template v-if="editingTitle">
					<Input v-model="editTitleValue" class="flex-1 max-w-[60%] h-8 text-sm" @keyup.enter="saveTitle" @keyup.escape="editingTitle = false" />
					<Button size="sm" variant="default" class="h-7 px-2" :disabled="savingField" @click="saveTitle">
						<Save class="h-3.5 w-3.5" />
					</Button>
					<Button size="sm" variant="ghost" class="h-7 px-2" @click="editingTitle = false">
						<X class="h-3.5 w-3.5" />
					</Button>
				</template>
				<template v-else>
					<h1 class="text-title ml-1 lg:ml-2 font-normal tracking-tight truncate max-w-[50%]">
						{{ mediaInfo.title }}
					</h1>
					<button class="px-1 hover:text-primary transition-colors" @click="startEditTitle" title="Edit title">
						<PencilSquareIcon class="h-3 w-3 lg:h-4 lg:w-4" />
					</button>
				</template>

				<div class="text-body-sm">ID: {{ mediaInfo.id }}</div>

				<!-- Moderation badge -->
				<span class="px-2 py-0.5 rounded-full border text-xs font-medium" :class="moderationBadgeClass">
					{{ moderationLabel }}
				</span>

				<button
					v-if="authenticityVerdict"
					:class="[authenticityVerdict.classes, 'ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity']"
					@click="selectedTab = 4"
				>
					<component :is="authenticityVerdict.icon" class="h-3.5 w-3.5" />
					{{ authenticityVerdict.label }}
				</button>
			</div>

			<!-- Media Preview Section -->
			<div class="p-2 lg:p-4 border-b flex-none">
				<div class="flex flex-col items-center">
					<div v-if="mediaInfo.id !== 0" class="w-full flex justify-center">
						<media-thumb
							:media-item="mediaInfo"
							class="max-w-full"
							:percentage="mediaInfo.statusInfo?.conversion_percentage"
							:size="128"
						/>
					</div>

					<!-- Action buttons row -->
					<div class="flex flex-wrap justify-end gap-2 pt-3 w-full">
						<!-- Transfer To -->
						<DropdownMenu v-if="!mmMode && transferList.length > 0">
							<DropdownMenuTrigger as-child>
								<Button variant="outline" class="h-8 lg:h-10 p-1 px-2 gap-1 text-xs">
									<ArrowUpTrayIcon class="h-4 w-4" /> Transfer To
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<DropdownMenuLabel class="text-xs text-muted-foreground">Transfer Destinations</DropdownMenuLabel>
								<DropdownMenuSeparator />
								<template v-for="(transfer, index) in transferList" :key="index">
									<DropdownMenuItem v-if="isTransferSupported(transfer)" class="text-xs gap-2" @click="publishToTransfer(transfer.id, transfer.name)">
										<CloudUpload class="h-3.5 w-3.5" /> {{ transfer.name }}
									</DropdownMenuItem>
								</template>
							</DropdownMenuContent>
						</DropdownMenu>

						<!-- Download -->
						<Button variant="outline" class="h-8 lg:h-10 p-1 px-2 gap-1 text-xs" @click="downloadMedia">
							<CloudArrowDownIcon class="h-4 w-4" /> <span class="hidden sm:inline">Download</span>
						</Button>

						<!-- Translate -->
						<Button variant="outline" class="h-8 lg:h-10 p-1 px-2 gap-1 text-xs" @click="openTranslateModal">
							<Languages class="h-4 w-4" /> {{ translateButtonLabel }}
						</Button>

						<!-- Actions dropdown (full menu) -->
						<DropdownMenu v-if="!mmMode">
							<DropdownMenuTrigger as-child>
								<Button variant="default" class="h-8 lg:h-10 p-1 px-3 gap-1 text-xs">
									Actions <ChevronDown class="h-3.5 w-3.5" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent class="w-52">
								<!-- Moderation -->
								<DropdownMenuLabel class="text-xs text-muted-foreground">Moderation</DropdownMenuLabel>
								<DropdownMenuItem v-if="mediaInfo.moderationStatus !== 1" class="text-xs gap-2" @click="approveMedia">
									<Check class="h-3.5 w-3.5" /> Approve
								</DropdownMenuItem>
								<DropdownMenuItem v-if="mediaInfo.moderationStatus !== 3" class="text-xs gap-2" @click="denyMedia">
									<X class="h-3.5 w-3.5" /> Deny
								</DropdownMenuItem>
								<DropdownMenuItem class="text-xs gap-2" @click="trashMedia">
									<Trash2 class="h-3.5 w-3.5" /> Trash
								</DropdownMenuItem>
								<DropdownMenuItem v-if="mediaInfo.moderationStatus !== 2" class="text-xs gap-2" @click="unmoderateMedia">
									<ArrowPathIcon class="h-3.5 w-3.5" /> Unmoderate
								</DropdownMenuItem>
								<DropdownMenuItem v-if="isTrashed" class="text-xs gap-2" @click="untrashMedia">
									<RefreshCw class="h-3.5 w-3.5" /> Un-Trash
								</DropdownMenuItem>
								<DropdownMenuItem class="text-xs gap-2" @click="hideMedia">
									<EyeSlashIcon class="h-3.5 w-3.5" /> Hide
								</DropdownMenuItem>
								<DropdownMenuItem class="text-xs gap-2" @click="cloneMediaData">
									<DocumentDuplicateIcon class="h-3.5 w-3.5" /> Clone Data
								</DropdownMenuItem>

								<DropdownMenuSeparator />

								<!-- Management -->
								<DropdownMenuLabel class="text-xs text-muted-foreground">Management</DropdownMenuLabel>
								<DropdownMenuItem class="text-xs gap-2" @click="showUploadChild = true">
									<Upload class="h-3.5 w-3.5" /> Upload Child
								</DropdownMenuItem>
								<DropdownMenuItem class="text-xs gap-2" @click="showReplaceAsset = true">
									<Replace class="h-3.5 w-3.5" /> Replace Asset
								</DropdownMenuItem>
								<DropdownMenuItem class="text-xs gap-2" @click="showAssignParent = true">
									<LinkIcon class="h-3.5 w-3.5" /> Assign Parent
								</DropdownMenuItem>
								<DropdownMenuItem class="text-xs gap-2" @click="reconvertMedia">
									<RefreshCw class="h-3.5 w-3.5" /> Re-Convert
								</DropdownMenuItem>
								<DropdownMenuItem v-if="isVideo || isImage" class="text-xs gap-2" @click="showThumbDialog = true">
									<Image class="h-3.5 w-3.5" /> Set Thumbnail
								</DropdownMenuItem>
								<DropdownMenuItem class="text-xs gap-2" @click="selectedTab = 3">
									<FileText class="h-3.5 w-3.5" /> Custom Data
								</DropdownMenuItem>

								<DropdownMenuSeparator />

								<DropdownMenuItem class="text-xs gap-2 text-destructive" @click="permanentlyDelete">
									<Trash2 class="h-3.5 w-3.5" /> Permanently Delete
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>
			</div>

			<!-- Additional Information Panel -->
			<div class="border-b">
				<button class="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted/50 transition-colors" @click="showAdditionalInfo = !showAdditionalInfo">
					<span class="flex items-center gap-2 font-medium">
						<Info class="h-4 w-4" /> Additional Information
					</span>
					<ChevronDown class="h-4 w-4 transition-transform" :class="{ 'rotate-180': showAdditionalInfo }" />
				</button>
				<div v-if="showAdditionalInfo" class="px-3 pb-3">
					<div class="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
						<div class="flex justify-between py-1 border-b border-dashed border-muted">
							<span class="text-muted-foreground">Created</span>
							<span>{{ formatDate(mediaInfo.upload) }}</span>
						</div>
						<div class="flex justify-between py-1 border-b border-dashed border-muted">
							<span class="text-muted-foreground">Views</span>
							<span>{{ mediaInfo.hits ?? 0 }}</span>
						</div>
						<div class="flex justify-between py-1 border-b border-dashed border-muted">
							<span class="text-muted-foreground">Visibility</span>
							<span>{{ statusLabel }}</span>
						</div>
						<div class="flex justify-between py-1 border-b border-dashed border-muted">
							<span class="text-muted-foreground">Moderation</span>
							<span>{{ moderationLabel }}</span>
						</div>
						<div class="flex justify-between py-1 border-b border-dashed border-muted">
							<span class="text-muted-foreground">File Type</span>
							<span>{{ filetypeLabel }} / {{ mediaInfo.contenttype }}</span>
						</div>
						<div class="flex justify-between py-1 border-b border-dashed border-muted">
							<span class="text-muted-foreground">Extension</span>
							<span>.{{ mediaInfo.extension }}</span>
						</div>
						<div v-if="mediaInfo.injector" class="flex justify-between py-1 border-b border-dashed border-muted">
							<span class="text-muted-foreground">Upload Method</span>
							<span>{{ mediaInfo.injector }}</span>
						</div>
						<div v-if="mediaInfo.parentid && mediaInfo.parentid > 0" class="flex justify-between py-1 border-b border-dashed border-muted">
							<span class="text-muted-foreground">Parent ID</span>
							<RouterLink :to="'/media/' + mediaInfo.parentid" class="text-primary">{{ mediaInfo.parentid }}</RouterLink>
						</div>
						<div v-if="mediaInfo.channel" class="flex justify-between py-1 border-b border-dashed border-muted">
							<span class="text-muted-foreground">Channel</span>
							<span>{{ mediaInfo.channel }}</span>
						</div>
					</div>

					<!-- Version selector -->
					<div v-if="!mmMode" class="mt-3 flex items-center gap-2">
						<GitBranch class="h-4 w-4 text-muted-foreground" />
						<span class="text-sm text-muted-foreground">Version:</span>
						<select
							:value="selectedVersion"
							@change="switchVersion(($event.target as HTMLSelectElement).value)"
							class="text-sm border rounded px-2 py-1 bg-background"
						>
							<option value="">Latest</option>
							<option value="original">Original</option>
						</select>
					</div>
				</div>
			</div>

			<!-- Activities Table (hidden in mmMode) -->
			<div v-if="!mmMode" class="overflow-x-auto hidden md:block">
				<Table class="min-w-full">
					<TableHeader class="border border-background">
						<TableRow>
							<TableHead class="text-xs lg:text-sm">Activities</TableHead>
							<TableHead class="text-xs lg:text-sm">Status</TableHead>
							<TableHead class="text-xs lg:text-sm">Time</TableHead>
							<TableHead class="text-xs lg:text-sm">User</TableHead>
							<TableHead class="text-xs lg:text-sm">Action</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						<TableRow class="border border-card">
							<TableCell class="text-xs lg:text-sm">Conversion</TableCell>
							<TableCell class="text-xs lg:text-sm">{{ mediaInfo.status <= 2 ? 'In Progress' : 'Completed' }}</TableCell>
							<TableCell class="text-xs lg:text-sm">{{ formatDate(mediaInfo.upload) }}</TableCell>
							<TableCell class="text-xs lg:text-sm">System</TableCell>
							<TableCell>
								<Button size="sm" class="text-xs h-7" @click="reconvertMedia">Re-Convert</Button>
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</div>
		</div>

		<!-- Side Panel — 30% on desktop -->
		<div class="lg:w-[30%] lg:shrink-0">
			<!-- Tab Navigation -->
			<div class="border-b py-1 lg:py-2 px-2 lg:px-4">
				<div class="flex overflow-x-auto items-center hide-scrollbar">
					<div
						v-for="menuItem in mediaMenuOptions"
						:key="menuItem.value"
						class="text-body hover:border-b-2 hover:border-secondary whitespace-nowrap pb-1 px-2"
						:class="{ 'border-b-2 border-primary': selectedTab === menuItem.value }"
					>
						<button @click.prevent="selectedTab = menuItem.value">{{ menuItem.label }}</button>
					</div>
				</div>
			</div>

			<!-- ═══ Details Tab ═══ -->
			<div v-if="selectedTab === 0" class="p-2 lg:p-3 space-y-4">
				<!-- Contributor -->
				<div>
					<h2 class="text-subtitle mb-1">Contributor</h2>
					<div class="flex items-center gap-3 ml-1">
						<img v-if="mediaInfo.uid !== 0" :src="'/services/useravatar/' + mediaInfo.uid + '/sm'" class="rounded-full h-10 w-10" />
						<div>
							<RouterLink :to="'/member/' + mediaInfo.uid" class="text-sm text-primary hover:underline">
								{{ mediaInfo.user_email }}
							</RouterLink>
							<div v-if="mediaInfo.user_firstname" class="text-xs text-muted-foreground">
								{{ mediaInfo.user_firstname }} {{ mediaInfo.user_lastname }}
							</div>
						</div>
					</div>
				</div>

				<!-- Description (editable) -->
				<div>
					<div class="flex items-center gap-2 mb-1">
						<h2 class="text-subtitle">Description</h2>
						<button v-if="!editingDescription" class="p-0.5 rounded hover:bg-muted" @click="startEditDescription" title="Edit description">
							<PencilSquareIcon class="h-3 w-3 text-muted-foreground hover:text-foreground" />
						</button>
					</div>
					<template v-if="editingDescription">
						<textarea
							v-model="editDescriptionValue"
							rows="4"
							class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						/>
						<div class="flex gap-1.5 mt-1.5">
							<Button size="sm" class="h-7 px-2 text-xs gap-1" :disabled="savingField" @click="saveDescription">
								<Loader2 v-if="savingField" class="h-3 w-3 animate-spin" />
								<Save v-else class="h-3 w-3" /> Save
							</Button>
							<Button size="sm" variant="ghost" class="h-7 px-2 text-xs" @click="editingDescription = false">Cancel</Button>
						</div>
					</template>
					<p v-else class="ml-1 text-body-sm text-muted-foreground">
						{{ stripHtml(mediaInfo.message) || 'No description' }}
					</p>
				</div>

				<!-- Tags -->
				<div>
					<h2 class="text-subtitle mb-1">Tags</h2>
					<TagDisplay v-model="tagList" @tag-added="handleTagAdded" @tag-removed="handleTagRemoved" />
				</div>
			</div>

			<!-- ═══ Related Tab ═══ -->
			<div v-if="selectedTab === 1" class="p-1 lg:p-2">
				<div class="flex border-b mb-3">
					<button
						class="px-3 py-1.5 text-sm font-medium transition-colors"
						:class="relatedSubTab === 'children' ? 'border-b-2 border-primary text-foreground' : 'text-muted-foreground hover:text-foreground'"
						@click="relatedSubTab = 'children'"
					>
						Children
					</button>
					<button
						class="px-3 py-1.5 text-sm font-medium transition-colors"
						:class="relatedSubTab === 'subtitles' ? 'border-b-2 border-primary text-foreground' : 'text-muted-foreground hover:text-foreground'"
						@click="relatedSubTab = 'subtitles'"
					>
						Subtitles / Captions
					</button>
				</div>

				<div v-if="relatedSubTab === 'children'">
					<div v-if="!childrenLoaded" class="text-center py-6 text-muted-foreground text-sm">Loading children...</div>
					<div v-else-if="childMedia.length === 0" class="text-center py-6 text-muted-foreground text-sm">No child media found.</div>
					<div v-else class="space-y-2">
						<div v-for="child in childMedia" :key="child.id"
							class="flex items-center gap-3 p-2 rounded border hover:bg-muted/50 cursor-pointer transition-colors"
							@click="router.push('/media/' + child.id)"
						>
							<img :src="getChildThumbSrc(child)" class="h-12 w-12 object-cover rounded border flex-shrink-0" />
							<div class="flex-1 min-w-0">
								<div class="text-sm font-medium truncate">{{ child.title || 'Untitled' }}</div>
								<div class="text-xs text-muted-foreground flex gap-2">
									<span>ID: {{ child.id }}</span>
									<span class="px-1.5 py-0.5 bg-muted rounded text-[10px] uppercase">{{ getChildTypeLabel(child) }}</span>
									<span v-if="child.extension">.{{ child.extension }}</span>
								</div>
								<div v-if="child.user_firstname" class="text-xs text-muted-foreground">By: {{ child.user_firstname }} {{ child.user_lastname }}</div>
							</div>
						</div>
					</div>
				</div>

				<div v-if="relatedSubTab === 'subtitles'">
					<div v-if="!childrenLoaded" class="text-center py-6 text-muted-foreground text-sm">Loading captions...</div>
					<div v-else-if="captionMedia.length === 0" class="text-center py-6 text-muted-foreground text-sm">No subtitles or captions found.</div>
					<Table v-else class="w-full">
						<TableHeader>
							<TableRow>
								<TableHead class="text-xs">Language / Title</TableHead>
								<TableHead class="text-xs">Type</TableHead>
								<TableHead class="text-xs">Date</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<TableRow v-for="cap in captionMedia" :key="cap.id" class="cursor-pointer hover:bg-muted/50" @click="router.push('/media/' + cap.id)">
								<TableCell class="text-sm">{{ cap.title || 'Untitled' }} <span class="ml-1 text-xs text-muted-foreground">.{{ cap.extension }}</span></TableCell>
								<TableCell class="text-xs text-muted-foreground">{{ cap.contenttype }}</TableCell>
								<TableCell class="text-xs text-muted-foreground">{{ cap.upload }}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</div>
			</div>

			<!-- ═══ Location Tab ═══ -->
			<div v-if="selectedTab === 2" class="p-1 lg:p-2">
				<div class="flex items-center gap-2 mb-2">
					<MapPinIcon class="h-5 w-5 text-primary" />
					<h1 class="text-base lg:text-lg font-light">Location</h1>
				</div>
				<div v-if="mediaInfo.geo_latitude && mediaInfo.geo_longitude" class="text-xs text-muted-foreground mb-2">
					Lat: {{ Number(mediaInfo.geo_latitude).toFixed(6) }}, Lng: {{ Number(mediaInfo.geo_longitude).toFixed(6) }}
				</div>
				<div v-else class="text-xs text-muted-foreground mb-2">No location set. Click on the map to add a marker.</div>
				<div ref="mapContainer" class="w-full border rounded" style="height: 350px" />
			</div>

			<!-- ═══ Metadata Tab ═══ -->
			<div v-if="selectedTab === 3" class="p-1 lg:p-2">
				<div class="flex justify-between items-center">
					<h1 class="text-base lg:text-lg">Metadata</h1>
					<Dialog v-model:open="metaDialogOpen">
						<Button variant="outline" size="sm" class="h-7 px-2 text-xs" @click="metaDialogOpen = true; getMetaProvider()">
							<PencilSquareIcon class="h-3.5 w-3.5 mr-1" /> Edit Fields
						</Button>
						<DialogContent class="!max-w-none w-[90vw] h-[90vh] flex flex-col p-0">
							<DialogHeader class="shrink-0 px-6 pt-6 pb-3 border-b pr-12">
								<div class="flex items-center justify-between">
									<div>
										<DialogTitle class="text-base">Edit Metadata</DialogTitle>
										<DialogDescription class="text-xs mt-0.5">Update custom fields for this media item</DialogDescription>
									</div>
									<div class="flex items-center gap-2">
										<Button size="sm" @click="saveMetadata()" class="text-xs h-8">Save Changes</Button>
										<DialogClose as-child>
											<Button size="sm" variant="outline" class="text-xs h-8">Cancel</Button>
										</DialogClose>
									</div>
								</div>
							</DialogHeader>
							<div class="overflow-y-auto flex-1 px-6 py-4">
								<MediaMetaData
									:metadata="mediaInfo.metadata"
									surface="modal"
									page="mediaitem"
									:providers="metaProviders"
									:groups="defaultData"
									:hidden-fields="hiddenMetaFields"
									@change="(e: any) => { mediaInfo.metadata[e.key] = e.value }"
								/>
							</div>
						</DialogContent>
					</Dialog>
				</div>

				<!-- Inline metadata view with tabs -->
				<div class="space-y-2 lg:space-y-4 w-full mt-2">
					<div class="flex gap-1 border-b pb-1 overflow-x-auto">
						<button
							v-for="(tabName, idx) in ['User', 'System', ...(mediaInfo.metadata?.exif ? ['EXIF'] : []), 'Request']"
							:key="idx"
							class="px-3 py-1 text-xs font-medium transition-colors whitespace-nowrap"
							:class="metaProviderTab === idx ? 'border-b-2 border-primary text-foreground' : 'text-muted-foreground hover:text-foreground'"
							@click="metaProviderTab = idx"
						>
							{{ tabName }}
						</button>
					</div>

					<!-- User metadata -->
					<div v-if="metaProviderTab === 0" class="space-y-2">
						<template v-for="(value, label) in mediaInfo.metadata" :key="label">
							<div v-if="typeof label === 'string' && !['exif','system','request','xmp','iptc'].includes(label)" class="grid w-full items-center gap-1">
								<Label :for="String(label)" class="text-xs lg:text-sm">{{ label }}</Label>
								<Input type="text" :default-value="value" class="w-full text-xs lg:text-sm" />
							</div>
						</template>
					</div>

					<!-- System metadata -->
					<div v-if="metaProviderTab === 1" class="space-y-2">
						<template v-for="(value, label) in mediaInfo.metadata?.system" :key="label">
							<div v-if="String(label) !== 'currentVersion'" class="grid w-full items-center gap-1">
								<Label :for="String(label)" class="text-xs lg:text-sm">{{ label }}</Label>
								<div v-if="typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value)" v-html="value" />
								<img v-else-if="typeof value === 'string' && /\.(jpe?g|png|gif|webp|svg)(\?.*)?$/i.test(value)" :src="value" alt="" class="max-w-full rounded border" />
								<Input v-else type="text" :value="value" readonly class="text-xs lg:text-sm" />
							</div>
						</template>
					</div>

					<!-- EXIF metadata -->
					<div v-if="metaProviderTab === 2 && mediaInfo.metadata?.exif" class="space-y-2">
						<template v-for="(entry, index) in mediaInfo.metadata?.exif" :key="index">
							<div class="grid w-full items-center gap-1">
								<Label class="text-xs lg:text-sm">{{ Array.isArray(entry) ? entry[1] : index }}</Label>
								<Input type="text" :default-value="Array.isArray(entry) ? entry[0] : entry" readonly class="text-xs lg:text-sm" />
							</div>
						</template>
					</div>

					<!-- Request metadata -->
					<div v-if="metaProviderTab === (mediaInfo.metadata?.exif ? 3 : 2)" class="space-y-2">
						<template v-for="(entry, index) in mediaInfo.metadata?.request" :key="index">
							<div class="grid w-full items-center gap-1">
								<Label class="text-xs lg:text-sm">{{ Array.isArray(entry) ? entry[1] : index }}</Label>
								<Input type="text" :default-value="Array.isArray(entry) ? entry[0] : entry" readonly class="text-xs lg:text-sm" />
							</div>
						</template>
					</div>
				</div>
			</div>

			<!-- ═══ Verify Tab ═══ -->
			<div v-if="selectedTab === 4" class="p-1 lg:p-2">
				<div class="flex items-center gap-2 mb-3">
					<ShieldCheckIcon class="h-5 w-5 text-primary" />
					<h1 class="text-base lg:text-lg font-light">Authenticity Report</h1>
				</div>
				<DeepSafeScan :system-meta="mediaInfo.metadata?.system" />
			</div>
		</div>
	</div>

	<!-- ═══ Set Thumbnail Dialog ═══ -->
	<Dialog v-model:open="showThumbDialog">
		<DialogContent class="sm:max-w-xl">
			<DialogHeader>
				<DialogTitle>Set Thumbnail</DialogTitle>
				<DialogDescription>Scrub through the video to find the perfect frame, then click Set Thumbnail.</DialogDescription>
			</DialogHeader>
			<div class="space-y-3 py-2">
				<!-- Video.js player for scrubbing -->
				<div v-if="isVideo && showThumbDialog" class="rounded-md overflow-hidden bg-black">
					<video ref="thumbVideoEl" class="video-js vjs-default-skin w-full" preload="auto" />
				</div>
				<!-- Current timecode display + manual override -->
				<div class="flex items-center gap-3">
					<div class="flex items-center gap-2 flex-1">
						<Label class="shrink-0 text-sm">Timecode</Label>
						<Input v-model="thumbTimecode" placeholder="00:00:05" class="font-mono" />
					</div>
					<span v-if="isVideo" class="text-xs text-muted-foreground whitespace-nowrap">
						<Clock class="h-3.5 w-3.5 inline -mt-0.5" /> {{ thumbCurrentTime }}
					</span>
				</div>
				<p v-if="!isVideo" class="text-xs text-muted-foreground">Enter a timecode (HH:MM:SS) to extract a frame.</p>
			</div>
			<DialogFooter>
				<Button variant="outline" @click="showThumbDialog = false">Cancel</Button>
				<Button :disabled="settingThumb || !thumbTimecode" @click="setThumbnail" class="gap-1.5">
					<Loader2 v-if="settingThumb" class="h-4 w-4 animate-spin" />
					<Image v-else class="h-4 w-4" />
					Set Thumbnail
				</Button>
			</DialogFooter>
		</DialogContent>
	</Dialog>

	<!-- ═══ Assign Parent Dialog ═══ -->
	<Dialog v-model:open="showAssignParent">
		<DialogContent class="sm:max-w-md">
			<DialogHeader>
				<DialogTitle>Assign Parent</DialogTitle>
				<DialogDescription>Enter the media ID to set as this item's parent.</DialogDescription>
			</DialogHeader>
			<div class="space-y-3 py-2">
				<div class="grid gap-2">
					<Label>Parent Media ID</Label>
					<Input v-model="assignParentId" placeholder="12345" type="number" />
				</div>
			</div>
			<DialogFooter>
				<Button variant="outline" @click="showAssignParent = false">Cancel</Button>
				<Button :disabled="!assignParentId" @click="doAssignParent" class="gap-1.5">
					<LinkIcon class="h-4 w-4" /> Assign
				</Button>
			</DialogFooter>
		</DialogContent>
	</Dialog>

	<!-- ═══ Upload Child Dialog ═══ -->
	<Dialog v-model:open="showUploadChild">
		<DialogContent class="sm:max-w-md">
			<DialogHeader>
				<DialogTitle>Upload Child Media</DialogTitle>
				<DialogDescription>Upload a file as a child of this media item.</DialogDescription>
			</DialogHeader>
			<div class="space-y-3 py-2">
				<div class="border-2 border-dashed rounded-lg p-8 text-center">
					<Upload class="h-8 w-8 mx-auto text-muted-foreground mb-2" />
					<p class="text-sm text-muted-foreground">Drag and drop a file here, or use the uploader with parent ID: <strong>{{ mediaInfo.id }}</strong></p>
				</div>
				<Button variant="outline" class="w-full" @click="router.push('/uploader?parentid=' + mediaInfo.id); showUploadChild = false">
					Open Uploader
				</Button>
			</div>
		</DialogContent>
	</Dialog>

	<!-- ═══ Replace Asset Dialog ═══ -->
	<Dialog v-model:open="showReplaceAsset">
		<DialogContent class="sm:max-w-md">
			<DialogHeader>
				<DialogTitle>Replace Asset</DialogTitle>
				<DialogDescription>Replace the media file while keeping all metadata and settings.</DialogDescription>
			</DialogHeader>
			<div class="space-y-3 py-2">
				<div class="border-2 border-dashed rounded-lg p-8 text-center">
					<Replace class="h-8 w-8 mx-auto text-muted-foreground mb-2" />
					<p class="text-sm text-muted-foreground">Upload a replacement file for media ID: <strong>{{ mediaInfo.id }}</strong></p>
				</div>
				<Button variant="outline" class="w-full" @click="router.push('/uploader?replaceid=' + mediaInfo.id); showReplaceAsset = false">
					Open Uploader
				</Button>
			</div>
		</DialogContent>
	</Dialog>

	<!-- ═══ AI Translate Dialog ═══ -->
	<Dialog v-model:open="showTranslateModal">
		<DialogContent class="sm:max-w-lg">
			<DialogHeader>
				<DialogTitle class="flex items-center gap-2">
					<Languages class="h-5 w-5" /> AI Translate
				</DialogTitle>
				<DialogDescription>
					<span v-if="translateStep === 'fields'">Select source, target language, and fields to translate.</span>
					<span v-else-if="translateStep === 'preview'">Review the AI-generated translations before applying.</span>
					<span v-else>Translation complete.</span>
				</DialogDescription>
			</DialogHeader>

			<!-- Step 1: Field selection -->
			<div v-if="translateStep === 'fields'" class="space-y-4 py-2">
				<!-- Source selector -->
				<div v-if="translateAvailableSources.length > 1" class="space-y-1.5">
					<label class="text-sm font-medium">Source text</label>
					<div class="flex flex-wrap gap-3">
						<label
							v-for="src in translateAvailableSources"
							:key="src.kind"
							class="flex items-center gap-2 text-sm cursor-pointer"
						>
							<input
								type="radio"
								:value="src.kind"
								v-model="translateSelectedSource"
								@change="loadTranslatableFields"
								class="accent-primary"
							/>
							{{ src.label }}
						</label>
					</div>
				</div>

				<!-- Target language -->
				<div class="space-y-1.5">
					<label class="text-sm font-medium">Target language</label>
					<select
						v-model="translateTargetLang"
						class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
					>
						<option value="">-- Select language --</option>
						<option value="de">German (de)</option>
						<option value="fr">French (fr)</option>
						<option value="es">Spanish (es)</option>
						<option value="it">Italian (it)</option>
						<option value="pt">Portuguese (pt)</option>
						<option value="nl">Dutch (nl)</option>
						<option value="pl">Polish (pl)</option>
						<option value="ru">Russian (ru)</option>
						<option value="ja">Japanese (ja)</option>
						<option value="zh">Chinese (zh)</option>
						<option value="ko">Korean (ko)</option>
						<option value="ar">Arabic (ar)</option>
						<option value="sv">Swedish (sv)</option>
						<option value="da">Danish (da)</option>
						<option value="fi">Finnish (fi)</option>
						<option value="cs">Czech (cs)</option>
						<option value="tr">Turkish (tr)</option>
					</select>
				</div>

				<!-- Fields to translate -->
				<div class="space-y-1.5">
					<label class="text-sm font-medium">Fields to translate</label>
					<div v-if="translateLoading" class="flex items-center gap-2 text-sm text-muted-foreground py-2">
						<Loader2 class="h-4 w-4 animate-spin" /> Loading fields…
					</div>
					<div v-else class="space-y-2">
						<label
							v-for="field in translateFields"
							:key="field.path"
							class="flex items-start gap-2 text-sm cursor-pointer"
						>
							<input
								type="checkbox"
								:value="field.path"
								v-model="translateSelectedFields"
								class="mt-0.5 accent-primary"
							/>
							<div>
								<div class="font-medium">{{ field.label }}</div>
								<div class="text-xs text-muted-foreground line-clamp-2">{{ field.value || 'No value' }}</div>
							</div>
						</label>
						<p v-if="!translateFields.length && !translateLoading" class="text-sm text-muted-foreground">No translatable fields found.</p>
					</div>
				</div>

				<p v-if="translateError" class="text-sm text-destructive">{{ translateError }}</p>
			</div>

			<!-- Step 2: Preview -->
			<div v-else-if="translateStep === 'preview'" class="space-y-3 py-2 max-h-[50vh] overflow-y-auto">
				<div
					v-for="field in translateFields.filter(f => translateSelectedFields.includes(f.path))"
					:key="field.path"
					class="rounded-md border p-3 space-y-1"
				>
					<div class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{{ field.label }}</div>
					<div class="text-xs text-muted-foreground line-through">{{ field.value || '—' }}</div>
					<div class="text-sm">{{ translatePreviews[field.path] || '—' }}</div>
				</div>
				<p v-if="translateLoading" class="flex items-center gap-2 text-sm text-muted-foreground">
					<Loader2 class="h-4 w-4 animate-spin" /> Generating preview…
				</p>
			</div>

			<!-- Step 3: Done -->
			<div v-else class="py-6 text-center space-y-2">
				<Check class="h-12 w-12 text-emerald-500 mx-auto" />
				<p class="text-sm font-medium">Translation applied successfully.</p>
			</div>

			<DialogFooter>
				<template v-if="translateStep === 'fields'">
					<Button variant="outline" @click="showTranslateModal = false">Cancel</Button>
					<Button
						:disabled="!translateTargetLang || !translateSelectedFields.length || translateLoading"
						@click="runTranslatePreview"
						class="gap-1.5"
					>
						<Loader2 v-if="translateLoading" class="h-4 w-4 animate-spin" />
						Preview Translation
					</Button>
				</template>
				<template v-else-if="translateStep === 'preview'">
					<Button variant="outline" @click="translateStep = 'fields'">Back</Button>
					<Button :disabled="translateApplying" @click="applyTranslation" class="gap-1.5">
						<Loader2 v-if="translateApplying" class="h-4 w-4 animate-spin" />
						Apply Translation
					</Button>
				</template>
				<template v-else>
					<Button @click="showTranslateModal = false">Close</Button>
				</template>
			</DialogFooter>
		</DialogContent>
	</Dialog>
</template>

<style>
@import 'leaflet/dist/leaflet.css';
</style>

<style scoped>
.hide-scrollbar {
	-ms-overflow-style: none;
	scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
	display: none;
}

@media (max-width: 640px) {
	.flex-col {
		flex-direction: column;
	}
	.basis-2\/3, .basis-1\/3 {
		flex-basis: 100%;
		width: 100%;
	}
}
</style>
